import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface LoadingAnimationProps {
  year: number;
  locationName: string;
  onComplete: () => void;
}

// 별 파티클 데이터를 고정 시드로 생성 (리렌더링마다 바뀌지 않도록)
const STARS = Array.from({ length: 40 }, (_, i) => ({
  id: i,
  size: ((i * 7 + 3) % 3) + 1,
  top: ((i * 137 + 11) % 100),
  left: ((i * 97 + 23) % 100),
  opacity: (((i * 53 + 7) % 6) + 1) / 10,
  duration: (((i * 31 + 5) % 3) + 2),
  delay: ((i * 19 + 3) % 2),
}));

export default function LoadingAnimation({ year, locationName, onComplete }: LoadingAnimationProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [visibleTexts, setVisibleTexts] = useState<string[]>([]);
  const [done, setDone] = useState(false);

  const steps = useMemo(() => [
    { text: t('loading.step1', { location: locationName }), duration: 700 },
    { text: t('loading.step2', { year }), duration: 800 },
    { text: t('loading.step3', { year }), duration: 700 },
    { text: t('loading.step4'), duration: 900 },
    { text: t('loading.step5'), duration: 700 },
    { text: t('loading.step6'), duration: 800 },
    { text: t('loading.step7'), duration: 600 },
  ], [t, year, locationName]);

  useEffect(() => {
    let stepIndex = 0;

    const runStep = () => {
      if (stepIndex >= steps.length) {
        setDone(true);
        setTimeout(onComplete, 900);
        return;
      }
      const step = steps[stepIndex];
      setCurrentStep(stepIndex);
      setVisibleTexts(prev => [...prev, step.text]);
      stepIndex++;
      setTimeout(runStep, step.duration);
    };

    const initTimer = setTimeout(runStep, 400);
    return () => clearTimeout(initTimer);
  }, []);

  const totalSteps = steps.length;
  const progressPct = Math.round(((currentStep + 1) / totalSteps) * 100);

  return (
    <div
      className="min-h-dvh flex flex-col bg-gray-950"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* 별 파티클 배경 */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {STARS.map(star => (
          <div
            key={star.id}
            className="absolute rounded-full bg-white"
            style={{
              width: star.size + 'px',
              height: star.size + 'px',
              top: star.top + '%',
              left: star.left + '%',
              opacity: star.opacity,
              animation: `pulse ${star.duration}s ease-in-out infinite`,
              animationDelay: star.delay + 's',
            }}
          />
        ))}
      </div>

      {/* 전체 콘텐츠 - 세로 중앙 정렬 */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-6 py-8 gap-0">

        {/* 로고 */}
        <img
          src="https://public.readdy.ai/ai/img_res/ff2a104a-70f3-4324-bb8d-41c2b9fa3434.png"
          alt="바른사주"
          className="h-7 object-contain mb-8 opacity-80"
        />

        {/* 애니메이션 아이콘 */}
        <div className="relative mb-8">
          <div
            className={`w-18 h-18 rounded-full border-2 border-amber-400/30 flex items-center justify-center ${done ? '' : 'animate-spin'}`}
            style={{ width: 72, height: 72, animationDuration: '3s' }}
          >
            <div className="w-14 h-14 rounded-full border-2 border-amber-400/50 flex items-center justify-center">
              <i className={`ri-sun-line text-amber-400 text-2xl ${done ? '' : 'animate-pulse'}`} />
            </div>
          </div>
          {!done && (
            <div
              className="absolute inset-0 rounded-full border-2 border-transparent border-t-amber-400"
              style={{ animation: 'spin 1.5s linear infinite' }}
            />
          )}
          {done && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-[72px] h-[72px] rounded-full bg-amber-400/20 flex items-center justify-center animate-ping absolute" />
            </div>
          )}
        </div>

        {/* 진행 텍스트 목록 */}
        <div className="w-full max-w-xs mb-6" style={{ minHeight: 196 }}>
          <div className="space-y-2">
            {visibleTexts.map((text, idx) => {
              const isLast = idx === visibleTexts.length - 1;
              return (
                <div
                  key={idx}
                  className="flex items-center gap-2.5 transition-all duration-500"
                  style={{ opacity: isLast ? 1 : 0.35 }}
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {isLast && !done ? (
                      <div className="w-3 h-3 rounded-full border-2 border-amber-400 border-t-transparent animate-spin" />
                    ) : (
                      <i className="ri-checkbox-circle-fill text-amber-400 text-sm" />
                    )}
                  </div>
                  <p className={`text-sm leading-snug ${isLast ? 'text-white' : 'text-white/40'}`}>{text}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* 진행 바 */}
        <div className="w-full max-w-xs">
          <div className="flex justify-between mb-1.5">
            <span className="text-white/40 text-xs">{t('loading.analyzing')}</span>
            <span className="text-amber-400 text-xs font-bold">{progressPct}%</span>
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-amber-400 rounded-full transition-all duration-700"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
